import { isSome } from '@mbfab/shared'
import { X } from 'lucide-react'
import type { ReactNode, RefObject } from 'react'
import { useId, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogPortal, DialogPrimitive } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/modal.md

const POPUP_MAX_WIDTH = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
} as const

// 業務画面のモーダル。Base UI の Dialog を使った同じ組み立て（portal 用のルート div → backdrop →
// Popup → 見出し → 本文 → フッター）を各ダイアログが手書きしていたので、ここに集約する。
//
// variant は既存の2系統をそのまま維持する（ScreenTabs の variant と同じ考え方）:
//   - 'compact': 確認・軽い一括操作のダイアログ（アイコン + 小さめの見出し + 数行の本文）
//   - 'card':    入力フォームのダイアログ（eyebrow + 大きな見出し + 閉じるボタン）
//
// 印刷ダイアログのような全画面2ペインのモーダルはまだこの2系統に収まらないため、対象外にしている
// （そちらは印刷まわりの共通化とあわせて別途）。
//
// 「開いている間だけマウントする」制御は呼び出し側で行う（前回の入力値を持ち越さないため、
// どのダイアログも `if (!open) return null` で中身ごと作り直している）。このコンポーネントは
// 常に開いている状態として描画する。
export function Modal({
  variant = 'compact',
  size = 'sm',
  icon,
  iconTone = 'primary',
  eyebrow,
  title,
  description,
  showCloseButton = variant === 'card',
  onClose,
  children,
  className,
  portalToBody = false,
  initialFocus,
  finalFocus,
}: {
  variant?: 'compact' | 'card'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** 見出し左のアイコン（compact のみ）。lucide のアイコンを size-4 で渡す */
  icon?: ReactNode
  iconTone?: 'primary' | 'destructive'
  /** 見出し上の英字ラベル（card のみ） */
  eyebrow?: string
  title: string
  /** 見出し直下の説明文（compact のみ） */
  description?: ReactNode
  showCloseButton?: boolean
  onClose: () => void
  children?: ReactNode
  className?: string
  /** sticky 要素内から開く画面では、祖先の stacking context を抜けるため body へ描画する */
  portalToBody?: boolean
  initialFocus?: boolean
  /** 起動した要素が閉じる前に消える場合、呼び出し側が指定する安定した復帰先 */
  finalFocus?: RefObject<HTMLElement | null>
}) {
  // Base UI の Dialog.Popup は Dialog.Portal 配下でないと例外になるため、DOM構造への影響を
  // 最小にする目的でこのコンポーネント自身の直下を portal 先にする（mbscan と同じ考え方）
  // ref の current は初回描画時には null。Base UI はこの時点の null を
  // body portal として解決するため、要素が確定してから portal を作る。
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  // 同一ページに複数マウントされ得るので、固定 ID ではなく useId で採番する
  const titleId = useId()

  return (
    <div ref={setRootElement}>
      <Dialog
        open
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            onClose()
          }
        }}
      >
        <DialogPortal container={portalToBody ? undefined : rootElement}>
          {/* Dialog.Backdrop は使わず、popup をプレーンな backdrop div の子として描画する
              （Chromium で backdrop-blur が popup 側へ回り込む問題を避けるため。mbscan と同じ理由） */}
          <div
            className={cn(
              'fixed inset-0 z-50 flex items-center justify-center bg-foreground/45 p-4',
              variant === 'compact' && 'backdrop-blur-[2px]',
            )}
          >
            {/* 入力欄のリングを自動表示せず、キーボードフォーカスをダイアログ内へ移すため */}
            <DialogPrimitive.Popup
              ref={popupRef}
              aria-labelledby={titleId}
              initialFocus={initialFocus ?? popupRef}
              finalFocus={finalFocus}
              className={cn(
                'w-full',
                variant === 'compact'
                  ? 'rounded-xl border border-border bg-popover p-5 text-sm text-popover-foreground shadow-2xl'
                  : 'rounded-2xl bg-card p-6 shadow-2xl',
                POPUP_MAX_WIDTH[size],
                className,
              )}
            >
              {variant === 'compact' ? (
                <div className="flex items-center gap-2.5">
                  {isSome(icon) && (
                    <span
                      className={cn(
                        'flex size-8 shrink-0 items-center justify-center rounded-lg',
                        iconTone === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-accent text-primary',
                      )}
                    >
                      {icon}
                    </span>
                  )}
                  <DialogPrimitive.Title id={titleId} render={<h2 />} className="text-base font-bold text-foreground">
                    {title}
                  </DialogPrimitive.Title>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div>
                    {isSome(eyebrow) && <p className="brand-eyebrow mb-2">{eyebrow}</p>}
                    <DialogPrimitive.Title id={titleId} render={<h2 />} className="text-xl font-black">
                      {title}
                    </DialogPrimitive.Title>
                  </div>
                  {showCloseButton && (
                    <Button variant="ghost" size="icon" aria-label="閉じる" onClick={onClose}>
                      <X />
                    </Button>
                  )}
                </div>
              )}

              {/* description は呼び出し側から任意の ReactNode（ConfirmDialog 経由で <div>/<ul> を含む
                  ブロック要素）を渡せる。<p> でラップすると HTML仕様上 <div>/<ul> を子に持てず、
                  React の DOM ネスト検証で console error になる（e2e の consoleErrorGuard が検出する）ため、
                  ブロック要素を子に取れる <div> でラップする */}
              {isSome(description) && <div className="mt-3 text-xs leading-5 text-muted-foreground">{description}</div>}

              {children}
            </DialogPrimitive.Popup>
          </div>
        </DialogPortal>
      </Dialog>
    </div>
  )
}

/** モーダル末尾のボタン行。フォームの submit ボタンを置く場合は form 要素の内側で使う */
export function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mt-4 flex justify-end gap-2', className)}>{children}</div>
}
